import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import {
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { ActionMenu, type ActionMenuItem } from "./ActionMenu.js";
import { TableTools } from "./TableTools.js";

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer owned by the app. Falls back to a primitive property. */
  cell?: (row: Row, index: number) => ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
}

export interface DataTableMobileRowContext {
  id: string;
  index: number;
  selected: boolean;
  selectable: boolean;
  toggleSelected: () => void;
  /** The block-owned selection checkbox, ready to place in the mobile card. */
  selectionCheckbox: ReactNode;
  /** The row's Actions menu, when `rowActions` returned items. */
  rowActions: ReactNode;
}

export interface DataTableBlockProps<Row> {
  rows: readonly Row[];
  columns: readonly DataTableColumn<Row>[];
  /** Stable identity for selection and React keys. */
  getRowId: (row: Row, index: number) => string;
  /** Controlled selection. Pair with `onSelectedIdsChange`. */
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (selectedIds: Set<string>) => void;
  /** Set false for a list with row actions but no selection/bulk actions. */
  selectable?: boolean;
  /** Rows that cannot be selected, e.g. paid or locked records. */
  isRowSelectable?: (row: Row) => boolean;
  selectAllLabel?: string;
  /** Human-readable row name for checkbox and Actions labels. */
  getRowLabel?: (row: Row, id: string) => string;

  loading?: boolean;
  loadingLabel?: string;
  /** True when the app has filtered an otherwise non-empty list. */
  filtered?: boolean;
  emptyState?: ReactNode;
  filteredEmptyState?: ReactNode;

  search?: ReactNode;
  filters?: ReactNode;
  tools?: ReactNode;
  extensionTools?: ReactNode;
  actions?: ReactNode;
  advancedFilters?: ReactNode;
  onRefresh?: () => void;
  refreshLabel?: string;
  refreshing?: boolean;
  refreshingLabel?: string;
  bulkActions?: readonly ActionMenuItem[];
  bulkActionsLabel?: string;
  bulkActionsAriaLabel?: string;
  toolbarLabel?: string;

  rowActions?: (
    row: Row,
    index: number,
  ) => readonly ActionMenuItem[] | null | undefined;
  rowActionsLabel?: string;
  rowActionsAriaLabel?: (row: Row) => string;
  actionsHeader?: ReactNode;

  /** Optional phone layout. Receives block-owned selection and row actions. */
  renderMobileRow?: (
    row: Row,
    context: DataTableMobileRowContext,
  ) => ReactNode;

  tableLabel?: string;
  testId?: string;
  className?: string;
  mobileClassName?: string;
}

function alignClass(align: DataTableColumn<unknown>["align"]): string | undefined {
  if (align === "end") return "text-right";
  if (align === "center") return "text-center";
  return undefined;
}

function defaultValue<Row>(row: Row, key: string): ReactNode {
  if (row === null || typeof row !== "object") return null;
  const value = (row as Record<string, unknown>)[key];
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "bigint") return value.toString();
  return null;
}

function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  label: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

/**
 * Reusable list-table block: controlled selection, toolbar, bulk actions,
 * row actions, and the standard loading/empty/filtered-empty states. It is a
 * composition block, not a page: columns and cell rendering stay in the app.
 */
export function DataTableBlock<Row>({
  rows,
  columns,
  getRowId,
  selectedIds,
  onSelectedIdsChange,
  selectable = true,
  isRowSelectable,
  selectAllLabel = "Select all eligible rows",
  getRowLabel,
  loading = false,
  loadingLabel = "Loading…",
  filtered = false,
  emptyState = "No rows yet.",
  filteredEmptyState = "No rows match these filters.",
  search,
  filters,
  tools,
  extensionTools,
  actions,
  advancedFilters,
  onRefresh,
  refreshLabel,
  refreshing,
  refreshingLabel,
  bulkActions,
  bulkActionsLabel,
  bulkActionsAriaLabel,
  toolbarLabel,
  rowActions,
  rowActionsLabel = "Actions",
  rowActionsAriaLabel,
  actionsHeader = "Actions",
  renderMobileRow,
  tableLabel,
  testId,
  className,
  mobileClassName,
}: DataTableBlockProps<Row>) {
  const entries = rows.map((row, index) => {
    const id = getRowId(row, index);
    const selectable = isRowSelectable ? isRowSelectable(row) : true;
    return {
      row,
      index,
      id,
      selectable,
      selected: selectedIds.has(id),
      label: getRowLabel?.(row, id) ?? id,
      actions: rowActions?.(row, index) ?? [],
    };
  });
  const eligibleIds = entries
    .filter((entry) => entry.selectable)
    .map((entry) => entry.id);
  const allSelected =
    eligibleIds.length > 0 &&
    eligibleIds.every((id) => selectedIds.has(id));
  const someSelected = eligibleIds.some((id) => selectedIds.has(id));
  const columnCount =
    columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) {
      for (const id of eligibleIds) next.delete(id);
    } else {
      for (const id of eligibleIds) next.add(id);
    }
    onSelectedIdsChange(next);
  };

  const toggleSelected = (id: string, selectable: boolean) => {
    if (!selectable) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange(next);
  };

  const selectionCheckbox = (entry: (typeof entries)[number]) =>
    selectable ? (
      <input
        type="checkbox"
        aria-label={`Select ${entry.label}`}
        checked={entry.selected}
        disabled={!entry.selectable}
        onChange={() => toggleSelected(entry.id, entry.selectable)}
      />
    ) : null;

  const rowActionsMenu = (entry: (typeof entries)[number]) => {
    if (entry.actions.length === 0) return null;
    return (
      <ActionMenu
        label={rowActionsLabel}
        ariaLabel={
          rowActionsAriaLabel?.(entry.row) ?? `Actions for ${entry.label}`
        }
        items={entry.actions}
      />
    );
  };

  const showToolbar = Boolean(
    search ||
      filters ||
      tools ||
      extensionTools ||
      actions ||
      advancedFilters ||
      onRefresh ||
      (selectable && bulkActions && bulkActions.length > 0) ||
      (selectable && selectedIds.size > 0),
  );

  const stateRow = (key: string, content: ReactNode) => (
    <TableRow key={key}>
      <TableCell colSpan={columnCount}>{content}</TableCell>
    </TableRow>
  );

  return (
    <div className={cn("grid gap-3", className)}>
      {showToolbar ? (
        <TableTools
          label={toolbarLabel}
          search={search}
          filters={filters}
          tools={tools}
          extensionTools={extensionTools}
          actions={actions}
          onRefresh={onRefresh}
          refreshLabel={refreshLabel}
          refreshing={refreshing}
          refreshingLabel={refreshingLabel}
          selectedCount={selectable ? selectedIds.size : 0}
          bulkActions={selectable ? bulkActions : undefined}
          bulkActionsLabel={bulkActionsLabel}
          bulkActionsAriaLabel={bulkActionsAriaLabel}
        >
          {advancedFilters}
        </TableTools>
      ) : null}

      <div
        className={cn(
          "surface overflow-hidden rounded-2xl",
          renderMobileRow && "hidden min-[64rem]:block",
        )}
        data-testid={testId ? `${testId}-table` : undefined}
      >
        <div className="table-scroll">
          <Table aria-label={tableLabel} aria-busy={loading || undefined}>
            <TableHeader>
              <TableRow>
                {selectable ? (
                  <TableHead className="w-10">
                    <SelectAllCheckbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      disabled={eligibleIds.length === 0}
                      label={selectAllLabel}
                      onChange={toggleAll}
                    />
                  </TableHead>
                ) : null}
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={cn(alignClass(column.align), column.className)}
                  >
                    {column.header}
                  </TableHead>
                ))}
                {rowActions ? (
                  <TableHead className="text-right">{actionsHeader}</TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? stateRow("loading", loadingLabel)
                : entries.length === 0
                  ? stateRow(
                      "empty",
                      filtered ? filteredEmptyState : emptyState,
                    )
                  : entries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        data-row-id={entry.id}
                        data-selected={entry.selected || undefined}
                      >
                        {selectable ? (
                          <TableCell className="w-10">
                            {selectionCheckbox(entry)}
                          </TableCell>
                        ) : null}
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            className={cn(
                              alignClass(column.align),
                              column.className,
                            )}
                          >
                            {column.cell
                              ? column.cell(entry.row, entry.index)
                              : defaultValue(entry.row, column.key)}
                          </TableCell>
                        ))}
                        {rowActions ? (
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {rowActionsMenu(entry)}
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {renderMobileRow ? (
        <div
          className={cn("grid gap-3 min-[64rem]:hidden", mobileClassName)}
          data-testid={testId ? `${testId}-stack` : undefined}
        >
          {loading ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {loadingLabel}
            </p>
          ) : entries.length === 0 ? (
            filtered ? (
              filteredEmptyState
            ) : (
              emptyState
            )
          ) : (
            entries.map((entry) => (
              <div key={entry.id} data-row-id={entry.id}>
                {renderMobileRow(entry.row, {
                  id: entry.id,
                  index: entry.index,
                  selected: entry.selected,
                  selectable: entry.selectable,
                  toggleSelected: () =>
                    toggleSelected(entry.id, entry.selectable),
                  selectionCheckbox: selectionCheckbox(entry),
                  rowActions: rowActionsMenu(entry),
                })}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
